import { iff } from '@digibee/control-statements';
import { Button } from '@heroui/react';
import { Plus } from 'iconoir-react';
import { head } from 'lodash';
import { FC } from 'react';

import * as Elements from './Header.elements';
import Select from '../../../components/SelectRealm';
import Action from '../../../Operation/components/Header/components/Action';
import { EnvironmentSelect } from '../../../Operation/components/Header/components/EnvironmentSelect';

import featureFlagConstants from '~/common/helpers/featureFlagConstants';
import i18n from '~/common/helpers/i18n';
import useContainPaths from '~/common/hooks/useContainPaths';
import useFeatureFlag from '~/common/hooks/useFeatureFlag';
import paths from '~/common/jsons/paths.json';
import { useHeaderContext } from '~/components/HeaderProvider';
import Role from '~/components/Role';
import { SubHeader, SubHeaderLink } from '~/components/SubHeader';
import Text from '~/components/Text';
import Actions from '~/scenes/Others/scenes/App/components/Actions';

interface HeaderProps {
  shadow?: boolean;
  setModalTwoFactor: () => void;
  setModalNewPassword: () => void;
  onSignOut: () => void;
  activeRealm: any;
  allIds: string[];
  byId: any;
  user: { email: string };
  hasCustomerIdentityProvider: string;
  license: any;
}

const Header: FC<HeaderProps> = ({
  shadow,
  setModalTwoFactor,
  setModalNewPassword,
  onSignOut,
  user,
  activeRealm,
  allIds,
  byId,
  hasCustomerIdentityProvider,
  license
}) => {
  const { configActionButton } = useHeaderContext();
  const containPaths = useContainPaths();
  const button = head(configActionButton);

  const { GOVERNANCE_AI_POLICY_TAB } = featureFlagConstants;
  const { treatments } = useFeatureFlag([GOVERNANCE_AI_POLICY_TAB]);

  return (
    <Elements.Container>
      <Elements.Top>
        <Elements.Left>
          <Select activeRealm={activeRealm.realm} allIds={allIds} byId={byId} />
        </Elements.Left>
        <Elements.Center>
          <Elements.Heading weight='regular' size='xs'>
            {/* @ts-expect-error */}
            {button?.text || button?.renderText?.()}
          </Elements.Heading>
        </Elements.Center>
        <Elements.Right>
          <Actions
            email={user?.email}
            setModalTwoFactor={setModalTwoFactor}
            setModalNewPassword={setModalNewPassword}
            onSignOut={onSignOut}
          />
        </Elements.Right>
      </Elements.Top>
      {/* @ts-expect-error */}
      <SubHeader shadow={shadow} isNotBorder>
        {containPaths([`${activeRealm.realm}/settings/api-keys`]) && (
          <EnvironmentSelect type='apiKeys' />
        )}
        {containPaths([`${activeRealm.realm}/settings/groups`]) && (
          <>
            <SubHeaderLink to={`/${activeRealm.realm}/settings/groups/list`}>
              <Text as='span'>{i18n.t('label.groups_list')}</Text>
            </SubHeaderLink>
            {hasCustomerIdentityProvider === 'true' && (
              <Role name='SAML-GROUP-MAPPING:READ'>
                <Elements.SubHeaderLinkWithBetaTag
                  to={`/${activeRealm.realm}/settings/groups/integrations`}
                  active={containPaths([
                    `${activeRealm.realm}/settings/groups/integrations`
                  ])}
                >
                  <Text as='span'>{i18n.t('label.groups_integration')}</Text>
                </Elements.SubHeaderLinkWithBetaTag>
              </Role>
            )}
          </>
        )}
        {containPaths([`${activeRealm.realm}/settings/identity-provider`]) && (
          <>
            <Role name='SSO-CONFIGURATION:READ'>
              <Elements.SubHeaderLinkWithBetaTag
                to={`/${activeRealm.realm}/settings/identity-provider/idp-settings`}
                active={containPaths([
                  `${activeRealm.realm}/settings/identity-provider/idp-settings`
                ])}
              >
                <Text as='span'>{i18n.t('action.idp_settings')}</Text>
              </Elements.SubHeaderLinkWithBetaTag>
            </Role>
            <Role name='SSO-CONFIGURATION:READ'>
              <Elements.SubHeaderLinkWithBetaTag
                to={`/${activeRealm.realm}/settings/identity-provider/authentication-rules`}
                active={containPaths([
                  `${activeRealm.realm}/settings/identity-provider/authentication-rules`
                ])}
              >
                <Text as='span'>{i18n.t('action.authentication_rules')}</Text>
              </Elements.SubHeaderLinkWithBetaTag>
            </Role>
          </>
        )}
        {containPaths([`${activeRealm.realm}/settings/capsules/headers`]) && (
          <SubHeaderLink
            disabled={containPaths(['PIPELINE:READ'])}
            to={`/${activeRealm.realm}/settings/capsules/headers`}
            active={containPaths([
              `${activeRealm.realm}/settings/capsules/headers`
            ])}
          >
            <Text as='span'>
              {i18n.t('scenes.beta.labels.upper_and_capitalize.capitalized', {
                item: `${i18n.t('noun.header_list_title')}`
              })}
            </Text>
          </SubHeaderLink>
        )}
        {containPaths([`${activeRealm.realm}/settings/ctl/token`]) && (
          <SubHeaderLink
            to='ctl/token'
            active={containPaths([`${activeRealm.realm}/settings/ctl/token`])}
          >
            <Text as='span'>Token</Text>
          </SubHeaderLink>
        )}
        {containPaths([`${activeRealm.realm}/settings/licensing`]) && (
          <SubHeaderLink
            to='licensing'
            active={containPaths([`${activeRealm.realm}/settings/licensing`])}
          >
            <Text
              as='span'
              data-testid='licensing-subheader-settings-plan-name'
            >
              {i18n.t(
                `label.${license?.planName?.toLowerCase().replace(/ /g, '_')}`
              )}
            </Text>
          </SubHeaderLink>
        )}
        {containPaths([`${activeRealm.realm}${paths.reasoning}`]) && (
          <Text
            as='span'
            type='primary'
            data-testid='licensing-subheader-settings-reasoning-model'
          >
            {i18n.t('reasoning.model_banner')}
          </Text>
        )}
        {containPaths([`${activeRealm.realm}${paths.policies}`]) && (
          <>
            <SubHeaderLink
              to='policies/security'
              active={containPaths([
                `${activeRealm.realm}${paths.policies}/security`
              ])}
            >
              <Text
                as='span'
                data-testid='governance-subheader-settings-policies-security'
              >
                {i18n.t('action.security')}
              </Text>
            </SubHeaderLink>

            <SubHeaderLink
              to='policies/transformation'
              active={containPaths([
                `${activeRealm.realm}${paths.policies}/transformation`
              ])}
            >
              <Text
                as='span'
                data-testid='governance-subheader-settings-policies-transformation'
              >
                {i18n.t('action.transformation')}
              </Text>
            </SubHeaderLink>
            {iff(
              treatments[GOVERNANCE_AI_POLICY_TAB].treatment === 'on',
              () => (
                <>
                  <SubHeaderLink
                    to='policies/ai'
                    active={containPaths([
                      `${activeRealm.realm}${paths.policies}/ai`
                    ])}
                  >
                    <Text
                      as='span'
                      data-testid='governance-subheader-settings-policies-ai'
                    >
                      {i18n.t('label.artificial_intelligence')}
                    </Text>
                  </SubHeaderLink>
                </>
              )
            )}
            {iff(
              license?.planName === 'Consumption Based Model' ||
                license?.planName === 'Production Subscription',
              () => (
                <>
                  <SubHeaderLink
                    to='policies/deployments'
                    active={containPaths([
                      `${activeRealm.realm}${paths.policies}/deployments`
                    ])}
                  >
                    <Text
                      as='span'
                      data-testid='governance-subheader-settings-policies-deployments'
                    >
                      {i18n.t('label.deployments')}
                    </Text>
                  </SubHeaderLink>
                </>
              )
            )}
          </>
        )}
        {containPaths([`${activeRealm.realm}/settings/limits`]) && (
          <>
            <SubHeaderLink
              to='limits/global'
              active={containPaths([
                `${activeRealm.realm}/settings/limits/global`
              ])}
            >
              <Text
                active={containPaths([
                  `${activeRealm.realm}/settings/limits/global`
                ])}
                as='span'
                data-testid='licensing-subheader-settings-limits-global'
              >
                {i18n.t('label.global_capacity')}
              </Text>
            </SubHeaderLink>

            <SubHeaderLink
              to='limits/local'
              active={containPaths([
                `${activeRealm.realm}/settings/limits/local`
              ])}
            >
              <Text
                active={containPaths([
                  `${activeRealm.realm}/settings/limits/local`
                ])}
                as='span'
                data-testid='licensing-subheader-settings-limits-local'
              >
                {i18n.t('label.local_capacity')}
              </Text>
            </SubHeaderLink>
          </>
        )}

        <Role
          // @ts-expect-error
          name={button?.role}
          // @ts-expect-error
          environmentCapable={button?.environmentCapable}
        >
          {/* @ts-expect-error */}
          {button?.action && (
            <Elements.Action>
              <Button
                onPress={() =>
                  // @ts-expect-error
                  button?.action({ visible: true })
                }
                radius='full'
                size='md'
                color='secondary'
                // @ts-expect-error
                data-testid={button?.['data-testid']}
              >
                <Plus />
                {i18n.t('scenes.beta.actions.create')}
              </Button>
            </Elements.Action>
          )}
        </Role>
        {containPaths([`${activeRealm.realm}/settings/alert`]) && (
          <>
            <SubHeaderLink
              to='alert'
              active={containPaths([`${activeRealm.realm}/settings/alert`])}
            >
              <Text as='span'>{i18n.t('label.alerts_settings')}</Text>
            </SubHeaderLink>
            {/* @ts-expect-error */}
            <Role name={button?.role}>
              {/* @ts-expect-errors */}
              {button?.action && (
                <Elements.Action>
                  <Action configActionButton={configActionButton} />
                </Elements.Action>
              )}
            </Role>
          </>
        )}
        {containPaths([`${activeRealm.realm}/settings/credentials`]) && (
          // @ts-expect-error
          <Role name={button?.role}>
            {/* @ts-expect-error */}
            {button?.action && (
              <Elements.Action>
                <Button
                  onPress={() =>
                    // @ts-expect-error
                    button?.action({ visible: true })
                  }
                  radius='full'
                  size='md'
                  color='secondary'
                  // @ts-expect-error
                  data-testid={button?.['data-testid']}
                >
                  <Plus />
                  {i18n.t('scenes.beta.actions.create')}
                </Button>
              </Elements.Action>
            )}
          </Role>
        )}
      </SubHeader>
    </Elements.Container>
  );
};

export default Header;
